import {BaseManager} from "../../infrastructure/managers/base-manager";
import {LoggerUtils} from "../../infrastructure/utils/logger/logger-utils";
import {SiteConfig} from "../bootstrap/models/site-config";
import {DeviceStore} from "../app/device.store";
import {singleton} from "../../infrastructure/decorators/singleton";
import {DomUtils} from "../../infrastructure/utils/dom-utils";
import {makeObservableClass} from "../../infrastructure/decorators/observable";
import {API_TYPES, LOCAL_STORAGE_KEYS, METADATA_MANAGER, ROUTE_NAMES, TERMINATOR} from "../consts";
import {LocalStorageUtils} from "../../infrastructure/utils/local-storage.utils";
import {ENTITY_DESCRIPTION} from "../../modules/entity-description-module/entity-description.module.consts";
import {NavigationManager} from "./navigation-manager";
import {RouterController} from "../components/router/router-controller";
import {action, computed, observable} from "mobx";
import sportTypes from "../app/consts/sport-types";
import {SPORT_TYPES} from "../app/consts";
import {WebsiteModuleEntitiesContext} from "../../apps/website/models/website-module.entities-context";

@makeObservableClass
@singleton
export class MetadataManager extends BaseManager {
  @observable sections;

  static injectables = [...BaseManager.injectables, LocalStorageUtils, RouterController, NavigationManager, WebsiteModuleEntitiesContext];

  constructor(
    domUtils: DomUtils,
    logger: LoggerUtils,
    siteConfig: SiteConfig,
    deviceStore: DeviceStore,
    localStorageUtils: LocalStorageUtils,
    routerController: RouterController,
    navigationManager: NavigationManager,
    websiteModuleEntitiesContext: WebsiteModuleEntitiesContext
    ) {
    super(domUtils, logger, siteConfig, deviceStore);

    this.sections = [];
    this.localStorageUtils = localStorageUtils;
    this.routerController = routerController;
    this.navigationManager = navigationManager;
    this.websiteModuleEntitiesContext = websiteModuleEntitiesContext;
    this.lastRequestParams = null;
  }

  @action updateSections(sections) {
    this.sections = sections; // This is now allowed inside an action
  }

  @computed get titleSection() {
    return this.sections?.find(section => section.name.includes(ENTITY_DESCRIPTION.SECTION_NAME.metaTitle))
  }

  @computed get descriptionSection() {
    return this.sections?.find(section => section.name.includes(ENTITY_DESCRIPTION.SECTION_NAME.metaDescription))
  }

  get sportType(): String {
    const sportName: String = this.navigationManager.currentLinkModel.params.sportName;
    const isHomePage: Boolean = this.isAllScoresRoute && sportName === undefined;
    let sportType: Number = isHomePage ? SPORT_TYPES.football : this.getSportTypeIdByNameForUrl(sportName);

    return sportType;
  }

  get entityType(): String {
    let entityType = null;

    if(this.isGameRoute) {
      entityType = this.isGameScheduled ? METADATA_MANAGER.ENTITY_TYPE.preGame : METADATA_MANAGER.ENTITY_TYPE.postGame;
    } else {
      entityType = METADATA_MANAGER.ENTITY_TYPE[this.navigationManager?.currentLinkModel?.name];
    }

    return entityType;
  }

  get entityId() {
    let entityId = this.isGameRoute ? this.websiteModuleEntitiesContext.game.id : this.navigationManager.currentLinkModel.params.id

    return entityId;
  }

  get apiType(): String {
    return this.localStorageUtils.get(LOCAL_STORAGE_KEYS.apiType) || API_TYPES.webws
  }

  get sectionNames(): String {
    return `${ENTITY_DESCRIPTION.SECTION_NAME.metaTitle},${ENTITY_DESCRIPTION.SECTION_NAME.metaDescription}`;
  }

  get terminatorPrefix(): String {
    let prefix: String = TERMINATOR.DOMAIN_PREFIX.prod;
    const envType: String = this.localStorageUtils.get(LOCAL_STORAGE_KEYS.terminatorEnvType) || TERMINATOR.ENV_TYPES.prod;

    if (envType === TERMINATOR.ENV_TYPES.qa) {
      prefix = TERMINATOR.DOMAIN_PREFIX.qa;
    }

    return prefix;
  }

  get isAllScoresRoute() {
    return this.navigationManager?.currentLinkModel?.name === ROUTE_NAMES.allScores
  }

  get isGameRoute() {
    return this.navigationManager?.currentLinkModel?.name === ROUTE_NAMES.game
  }

  get isGameScheduled() {
    return this.routerController.currentRoute.metadata.game?.isScheduled
  }

  get validRoutes() {
    const allValidRoutes = Object.values(METADATA_MANAGER.ROUTE_NAMES);

    return allValidRoutes;
  }

  get hasSections() {
    return this.sections?.length > 0
  }

  isValidLanguage(id) {
    return TERMINATOR.LANGUAGES.includes(id)
  }

  getSportTypeIdByNameForUrl(nameForUrl) {
    const sport = sportTypes.sports.find(sport => sport.nameForURL === nameForUrl);

    return sport ? sport.id : null; // Return null if not found
  }
}
